import React, { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { generateSummaryReport } from "../services/apiService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getFileName } from "../utils/getFileName.js";
import { generateSummaryReportPDF } from "../services/apiService";
import "../css/reports.css";
const ReportTable = () => {
  const startYear = 2020;
  const currentYear = new Date().getFullYear();
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const handleYearChange = (event) => {
    const year = parseInt(event.target.value);
    setSelectedYear(year);
  };

  const fetchReportData = useCallback(async (year) => {
    setIsLoading(true);
    try {
      const response = await generateSummaryReport({ year: year });
      console.log("API Response format:", response);
      console.log("First month value type:", typeof response?.revenue?.Jan);

      if (response.status == true) {
        processReportData(response, year);
      } else {
        console.error("API returned false status");
        setRows([]);
        setColumns([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setRows([]);
      setColumns([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // Keep empty deps - function is stable

  const processReportData = (apiData, year) => {
    const shortYear = (year % 100).toString().padStart(2, "0");
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const rowData = [];
    let rowId = 0;

    // **FIX 1: Updated formatValue - 3 decimal places**
    const formatValue = (val) => {
      if (
        val === 0 ||
        val === "0" ||
        val === "0.000" ||
        val === null ||
        val === undefined
      ) {
        return 0;
      }
      let parsedValue;
      if (typeof val === "string") {
        parsedValue = parseFloat(val.replace(/,/g, ""));
      } else {
        parsedValue = parseFloat(val);
      }
      if (isNaN(parsedValue)) {
        return 0;
      }
      return Number(parsedValue.toFixed(3)); // ✅ FIX: Always 3 decimals
    };

    const rowDefinitions = [
      { key: "revenue", label: "Revenue", bold: false },
      { key: "salesCost", label: "Direct Cost / Cost Of Sales", bold: false },
      { key: "grossProfit", label: "Gross Profit", bold: true },
      {
        key: "administrativeExpenses",
        label: "Operations & Administrative Expenses",
        bold: false,
      },
      { key: "otherIncome", label: "Other Income", bold: false },
      { key: "exchangeLoss", label: "Exchange Loss", bold: false },
      {
        key: "assetSales",
        label: "Profit / Loss on disposal of assets",
        bold: false,
      },
      { key: "ebitda", label: "EBITDA", bold: true },
      { key: "depreciation", label: "Depreciation", bold: false },
      { key: "appreciation", label: "Appreciation", bold: false },
      { key: "ebit", label: "EBIT", bold: true },
      { key: "bankCharges", label: "Bank Charges", bold: false },
      { key: "ebt", label: "EBT", bold: true },
      { key: "tax", label: "Tax", bold: false },
      { key: "eat", label: "EAT", bold: false },
    ];

    rowDefinitions.forEach(({ key, label, bold }) => {
      const row = {
        id: rowId++,
        particular: label,
        isBold: bold,
        dataKey: key,
      };

      let total = 0;

      months.forEach((month) => {
        const monthValue = formatValue(apiData[key]?.[month]);
        row[month] = monthValue;
        total += monthValue;
      });

      // **FIX: Use special totals for specific rows**
      if (key === "appreciation") {
        row.FY = formatValue(apiData.appreciationTotal);
      } else if (key === "depreciation") {
        row.FY = formatValue(apiData.depreciationTotal);
      } else if (key === "tax") {
        row.FY = formatValue(apiData.taxTotal);
      } else {
        row.FY = Number(total.toFixed(3)); // Original calculation for all other rows
      }

      rowData.push(row);
    });

    const newColumns = [
      {
        field: "particular",
        headerName: "Particulars",
        width: 260,
        minWidth: 220,
        flex: 0.8,
        renderCell: (params) => (
          <span className={params.row.isBold ? "bold-text" : ""}>
            {params.value}
          </span>
        ),
        sortable: false,
      },
      ...months.map((month) => ({
        field: month,
        headerName: `${month}-${shortYear}`,
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        type: "number",
        align: "right",
        headerAlign: "center",
        sortable: false,
        // ✅ FIX: Raw number display (no comma)
        renderCell: (params) => {
          const value = Number(params.value);
          return value === 0 ? "0" : value.toFixed(3); // Same logic for all columns
        },
      })),
      {
        field: "FY",
        headerName: `FY${shortYear}`,
        width: 110,
        minWidth: 100,
        type: "number",
        align: "right",
        headerAlign: "center",
        sortable: false,
        // ✅ FIX: Show exactly 3 decimals, no comma
        renderCell: (params) => Number(params.value).toFixed(3),
      },
    ];

    setRows(rowData);
    setColumns(newColumns);

    // **ADD THIS: Log final processed data to verify**
    console.log("Processed rows:", rowData[0]); // Check first row format
    console.log("API raw data:", apiData.revenue); // Check API response format
  };

  // ONLY ONE useEffect for initial load + year change
  useEffect(() => {
    fetchReportData(selectedYear);
  }, [selectedYear]); // Remove fetchReportData from deps - useCallback makes it stable

  // useEffect(() => {
  //   fetchReportData(currentYear);
  // }, [fetchReportData]);

  // const handleExportExcel = async () => {
  //   const workbook = new ExcelJS.Workbook();
  //   const worksheet = workbook.addWorksheet(
  //     `Profit & Loss Report ${selectedYear}`
  //   );

  //   worksheet.columns = [
  //     { key: "particular", width: 35 },
  //     { key: "Jan", width: 12 },
  //     { key: "Feb", width: 12 },
  //     { key: "Mar", width: 12 },
  //     { key: "Apr", width: 12 },
  //     { key: "May", width: 12 },
  //     { key: "Jun", width: 12 },
  //     { key: "Jul", width: 12 },
  //     { key: "Aug", width: 12 },
  //     { key: "Sep", width: 12 },
  //     { key: "Oct", width: 12 },
  //     { key: "Nov", width: 12 },
  //     { key: "Dec", width: 12 },
  //     { key: "FY", width: 14 },
  //   ];

  //   const shortYear = (selectedYear % 100).toString().padStart(2, "0");
  //   const headerRow = [
  //     "Particulars",
  //     "Jan-".concat(shortYear),
  //     "Feb-".concat(shortYear),
  //     "Mar-".concat(shortYear),
  //     "Apr-".concat(shortYear),
  //     "May-".concat(shortYear),
  //     "Jun-".concat(shortYear),
  //     "Jul-".concat(shortYear),
  //     "Aug-".concat(shortYear),
  //     "Sep-".concat(shortYear),
  //     "Oct-".concat(shortYear),
  //     "Nov-".concat(shortYear),
  //     "Dec-".concat(shortYear),
  //     "FY".concat(shortYear),
  //   ];

  //   worksheet.addRow(headerRow);

  //   // Header styling FIRST (line ~45-55) - REMOVE bottom border
  //   worksheet.getRow(1).font = { bold: true, size: 12 };

  //   worksheet.getRow(1).alignment = {
  //     horizontal: "center",
  //     vertical: "middle",
  //   };
  //   // **FIX: Apply border ONLY to first 14 cells (A1-N1)**
  //   for (let col = 1; col <= 14; col++) {
  //     const cell = worksheet.getRow(1).getCell(col);
  //     cell.border = {
  //       top: { style: "thin" },
  //       left: { style: "thin" },
  //       bottom: { style: "thin" },
  //       right: { style: "thin" },
  //     };
  //   }

  //   // **NEW: Format values same as DataGrid**
  //   const formatCellValue = (value) => {
  //     const numValue = Number(value);
  //     return numValue === 0 ? 0 : Number(numValue.toFixed(3));
  //   };

  //   // Data rows - FIXED borders (lines ~60+)
  //   rows.forEach((row) => {
  //     const rowValues = [
  //       row.particular, // Particulars - no formatting
  //       formatCellValue(row.Jan),
  //       formatCellValue(row.Feb),
  //       formatCellValue(row.Mar),
  //       formatCellValue(row.Apr),
  //       formatCellValue(row.May),
  //       formatCellValue(row.Jun),
  //       formatCellValue(row.Jul),
  //       formatCellValue(row.Aug),
  //       formatCellValue(row.Sep),
  //       formatCellValue(row.Oct),
  //       formatCellValue(row.Nov),
  //       formatCellValue(row.Dec),
  //       formatCellValue(row.FY), // FY gets 3 decimals
  //     ];

  //     const excelRow = worksheet.addRow(rowValues);

  //     if (row.isBold) {
  //       excelRow.font = { bold: true };
  //     }
  //     excelRow.getCell(14).font = { bold: true };
  //     excelRow.alignment = { horizontal: "center", vertical: "middle" };

  //     // **FIXED: Borders ONLY columns A-N (1-14), every cell gets border**
  //     for (let col = 2; col <= 14; col++) {
  //       const cell = excelRow.getCell(col);
  //       cell.alignment = { horizontal: "center", vertical: "middle" };
  //       cell.numFmt = "0.000;(-0.000)"; // Excel format: 9550.000 or -2933.333
  //       cell.border = {
  //         top: { style: "thin" },
  //         left: { style: "thin" },
  //         bottom: { style: "thin" },
  //         right: { style: "thin" },
  //       };
  //     }
  //   });

  //   // **NEW: Add outer table border after all rows (replaces eachRow)**
  //   const lastRow = worksheet.rowCount;
  //   for (let col = 1; col <= 14; col++) {
  //     // Top border (header)
  //     worksheet.getCell(1, col).border = {
  //       ...worksheet.getCell(1, col).border,
  //       top: { style: "medium" },
  //     };
  //     // Bottom border (last row)
  //     worksheet.getCell(lastRow, col).border = {
  //       ...worksheet.getCell(lastRow, col).border,
  //       bottom: { style: "medium" },
  //     };
  //   }

  //   // Remove this old line:
  //   // worksheet.eachRow({ includeEmpty: false }, (row) => { row.height = 22; });
  //   worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
  //     row.height = 22;
  //   });

  //   worksheet.eachRow({ includeEmpty: false }, (row) => {
  //     row.height = 22;
  //   });

  //   const buffer = await workbook.xlsx.writeBuffer();
  //   const blob = new Blob([buffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });
  //   saveAs(blob, getFileName(`Profit & Loss Report ${selectedYear}.xlsx`));
  // };
  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      `Profit & Loss Report ${selectedYear}`
    );

    worksheet.columns = [
      { key: "particular", width: 35 },
      { key: "Jan", width: 12 },
      { key: "Feb", width: 12 },
      { key: "Mar", width: 12 },
      { key: "Apr", width: 12 },
      { key: "May", width: 12 },
      { key: "Jun", width: 12 },
      { key: "Jul", width: 12 },
      { key: "Aug", width: 12 },
      { key: "Sep", width: 12 },
      { key: "Oct", width: 12 },
      { key: "Nov", width: 12 },
      { key: "Dec", width: 12 },
      { key: "FY", width: 14 },
    ];

    const shortYear = (selectedYear % 100).toString().padStart(2, "0");
    const headerRow = [
      "Particulars",
      `Jan-${shortYear}`,
      `Feb-${shortYear}`,
      `Mar-${shortYear}`,
      `Apr-${shortYear}`,
      `May-${shortYear}`,
      `Jun-${shortYear}`,
      `Jul-${shortYear}`,
      `Aug-${shortYear}`,
      `Sep-${shortYear}`,
      `Oct-${shortYear}`,
      `Nov-${shortYear}`,
      `Dec-${shortYear}`,
      `FY${shortYear}`,
    ];

    worksheet.addRow(headerRow);

    // Header styling - ONLY columns A-N (1-14)
    worksheet.getRow(1).font = { bold: true, size: 12 };
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    for (let col = 1; col <= 14; col++) {
      const cell = worksheet.getRow(1).getCell(col);
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    }

    // **Data rows with conditional formatting + complete borders**
    rows.forEach((row) => {
      // Format values: 0→0, others→3 decimals
      const formatCellValue = (value) => {
        const numValue = Number(value);
        return numValue === 0 ? 0 : Number(numValue.toFixed(3));
      };

      const rowValues = [
        row.particular,
        formatCellValue(row.Jan),
        formatCellValue(row.Feb),
        formatCellValue(row.Mar),
        formatCellValue(row.Apr),
        formatCellValue(row.May),
        formatCellValue(row.Jun),
        formatCellValue(row.Jul),
        formatCellValue(row.Aug),
        formatCellValue(row.Sep),
        formatCellValue(row.Oct),
        formatCellValue(row.Nov),
        formatCellValue(row.Dec),
        formatCellValue(row.FY),
      ];

      const excelRow = worksheet.addRow(rowValues);

      // Bold styling
      if (row.isBold) {
        excelRow.font = { bold: true };
      }
      excelRow.getCell(14).font = { bold: true };

      // **Particulars column (A) - Left align + border**
      const particularCell = excelRow.getCell(1);
      particularCell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      particularCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // **Number columns (B-N) - Conditional formatting + borders**
      for (let col = 2; col <= 14; col++) {
        const cell = excelRow.getCell(col);
        const cellValue = rowValues[col - 1];

        // Conditional number formatting
        if (Number(cellValue) === 0) {
          cell.numFmt = "0"; // Plain 0
        } else {
          cell.numFmt = "0.000"; // Shows: -2933.333
        }

        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
    });

    // **Outer table border (top of header + bottom of last row)**
    const lastRow = worksheet.rowCount;
    for (let col = 1; col <= 14; col++) {
      // Top border (header row 1)
      worksheet.getCell(1, col).border = {
        ...worksheet.getCell(1, col).border,
        top: { style: "medium" },
      };
      // Bottom border (last data row)
      worksheet.getCell(lastRow, col).border = {
        ...worksheet.getCell(lastRow, col).border,
        bottom: { style: "medium" },
      };
    }

    // Set uniform row height
    worksheet.eachRow({ includeEmpty: true }, (row) => {
      row.height = 22;
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, getFileName(`Profit & Loss Report ${selectedYear}.xlsx`));
  };

  const getPDF = async () => {
    const payload = { year: selectedYear };
    console.log(payload, "payload_getReport");
    try {
      const response = await generateSummaryReportPDF(payload);
      console.log("getPettyCashReport", response);

      if (response?.pdfPath) {
        const pdfUrl = `${process.env.REACT_APP_ASSET_URL}${response.pdfPath}`;
        const pdfResponse = await fetch(pdfUrl);
        const pdfBlob = await pdfResponse.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = pdfBlobUrl;
        link.setAttribute("download", getFileName("Profit & Loss Report.pdf"));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfBlobUrl);
      }
    } catch (error) {
      console.error("Failed to fetch quotations:", error);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={400}
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading report data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <style jsx>{`
        .responsive-table-container {
          height: 70vh;
          max-height: 700px;
          width: 100%;
          overflow: auto;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .table-inner {
          min-width: 1450px;
          height: 100%;
        }
        .responsive-table-container::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }
        .responsive-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        .responsive-table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
        }
        .responsive-table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        .bold-text {
          font-weight: 600;
        }
        .fy-total {
          font-weight: 700 !important;
          color: #1976d2;
        }
        @media (max-width: 1400px) {
          .table-inner {
            min-width: 1350px;
          }
        }
        @media (max-width: 1200px) {
          .table-inner {
            min-width: 1250px;
          }
        }
      `}</style>

      <div className="row d-flex align-items-center justify-content-between m-3">
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label htmlFor="" className="form-label">
            Select year
          </label>
          <select
            name="year"
            className="form-select vesselbox"
            aria-label="Default select example"
            onChange={handleYearChange}
            value={selectedYear}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <div className="d-flex gap-2 justify-content-end">
            <button
              className="btn btn-info filbtnjob new_width"
              onClick={handleExportExcel}
            >
              Download Excel
            </button>
            <button
              className="btn btn-info filbtnjob new_width"
              onClick={getPDF}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="row my-2">
        <div className="col-12 profit-table-space">
          {/* FIXED: Single container with table-inner for VERTICAL SCROLL */}
          <div className="responsive-table-container newSpace-scroll">
            <div className="table-inner">
              <DataGrid
                rows={rows}
                columns={columns}
                getRowClassName={(params) =>
                  `report-row row-${params.index % 2}`
                }
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableColumnMenu
                hideFooter
                disableColumnResize
                // ✅ FIXED: Removed pagination & hideScrollbar - let container handle ALL scrolling
                // ✅ DataGrid will render ALL 15 rows, container provides scroll
                sx={{
                  border: "none",
                  fontSize: "0.825rem",
                  height: "100%",
                  width: "100%",
                  // ✅ ONLY disable DataGrid's HORIZONTAL scrollbar, keep VERTICAL free
                  "& .MuiDataGrid-virtualScroller": {
                    overflowX: "hidden", // Hide DataGrid horizontal scroll
                    overflowY: "visible", // Allow vertical content
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #dee2e6 !important",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                  },
                  "& .MuiDataGrid-cell": {
                    borderRight: "1px solid #e9ecef",
                    justifyContent: "flex-end",
                    "&:first-of-type": {
                      justifyContent: "flex-start !important",
                      paddingLeft: "12px",
                    },
                    '&[data-field="FY"]': {
                      backgroundColor: "#f0f8ff",
                      fontWeight: 600,
                    },
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f8f9ff",
                  },
                  // Ensure rows have proper height for scrolling
                  "& .MuiDataGrid-row": {
                    minHeight: "52px !important",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {rows.length === 0 && !isLoading && (
        <div className="text-center py-4">
          <Typography variant="h6" color="textSecondary">
            No data available for the selected year
          </Typography>
        </div>
      )}
    </>
  );
};

export default ReportTable;
